// ============================================================
// RequestResetScreen.tsx
// Forgot Password — step 1. User enters their email; on success we
// flip the auth view to Confirmation (handled by App.tsx via the
// onSuccess prop). The endpoint always returns success regardless of
// whether the email matched a user — account-enumeration prevention.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { ErrorNote } from '../shared/ActionBox'
import { requestPasswordResetApi } from '../../api/casevoApi'
interface RequestResetScreenProps {
  onShowLogin: () => void
  onSuccess: () => void
}
// Per-field validation errors. Mirrors LoginScreen's pattern — the single
// banner above the form is reserved for server-side errors (rate limits,
// network failures), per-field messages render inline below the input.
type FieldErrors = Partial<Record<'email', string>>
const RequestResetScreen: React.FC<RequestResetScreenProps> = ({ onShowLogin, onSuccess }) => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Empty until the user submits with invalid data — we deliberately do not
  // pre-flag the field on first render.
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  // Focus the email field as soon as the screen mounts so the user can
  // start typing without a click. Empty deps array → runs once.
  const emailRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    emailRef.current?.focus()
  }, [])
  // Runs all client-side rules and returns whichever messages applied.
  // Caller decides what to do with the result (set state vs. proceed).
  const validate = (): FieldErrors => {
    const errs: FieldErrors = {}
    if (!email.trim()) {
      errs.email = 'Please enter your email address'
    } else if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      errs.email = 'Please enter a valid email address'
    }
    return errs
  }
  // Clears the email field's error the moment the user starts editing again.
  const clearFieldError = (key: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Clear any stale server-error banner before re-validating, so the
    // user doesn't see a previous attempt's message sitting next to a
    // fresh per-field error.
    setError(null)
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      return
    }
    try {
      setLoading(true)
      setError(null)
      setFieldErrors({})
      await requestPasswordResetApi(email)
      onSuccess()
    } catch (err) {
      // Distinguish "the server responded with an error" from "we couldn't
      // reach the server". 429 gets a dedicated rate-limit message; any
      // other backend error message wins on its own; no-response branch
      // surfaces the generic connectivity message.
      if (axios.isAxiosError<{ error?: unknown }>(err) && err.response) {
        const backendMessage = err.response.data?.error
        if (err.response.status === 429) {
          setError('Too many reset requests. Please try again in an hour.')
        } else if (typeof backendMessage === 'string' && backendMessage.length > 0) {
          setError(backendMessage)
        } else {
          setError('Something went wrong. Please try again.')
        }
      } else {
        // No response received → network failure (offline, DNS, CORS,
        // timeout, server unreachable). Or a non-axios error, treated
        // the same way to be safe.
        setError('Could not connect to the server. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }
  // Base input class — border colour lives in fieldClass() below so we can
  // flip to red without fighting Tailwind's class-order resolution.
  const inputBase = `w-full border rounded-lg px-4 py-3
    text-base bg-casevo-input text-casevo-forest focus:outline-none
    focus:border-casevo-mid disabled:opacity-60 disabled:cursor-not-allowed`
  const fieldClass = (key: keyof FieldErrors) =>
    `${inputBase} ${fieldErrors[key] ? 'border-red-500' : 'border-casevo-border'}`
  // Inline spinner — `currentColor` lets it inherit the button's text colour
  // so it works on both the brand-green button and any future variants.
  const Spinner: React.FC = () => (
    <svg
      className="animate-spin h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
      />
    </svg>
  )
  return (
    <div className="min-h-screen bg-casevo-offwhite font-sans flex flex-col">
      {/* Nav bar */}
      <nav className="bg-casevo-header px-4 sm:px-6 h-14 flex items-center border-b border-casevo-border">
        <div className="flex items-center gap-3">
          <svg width="28" height="28" viewBox="0 0 68 68">
            <circle cx="34" cy="34" r="34" fill="#2d6a4f"/>
            <rect x="23" y="17" width="9" height="34" rx="4.5" fill="#ffffff"/>
            <rect x="36" y="22" width="9" height="24" rx="4.5" fill="#74c69d"/>
          </svg>
          <div className="flex flex-col">
            <span className="text-white font-medium text-sm tracking-widest uppercase leading-none">Casevo</span>
            <span className="text-casevo-light text-xs tracking-widest uppercase leading-none">Case Management</span>
          </div>
        </div>
      </nav>
      {/* Request reset form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm bg-casevo-surface border border-casevo-border rounded-[14px] shadow-sm p-6 sm:p-8">
          <div className="mb-6">
            <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">Password reset</p>
            <h1 className="text-2xl font-medium text-casevo-forest">Reset your password</h1>
          </div>
          <p className="text-sm text-casevo-mid mb-6">
            Enter your email and we'll send you a link to reset your password.
          </p>
          <form onSubmit={handleSubmit}>
            {error && (
              <ErrorNote className="mb-4">{error}</ErrorNote>
            )}
            <div className="mb-6">
              <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
                Email address
              </label>
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  clearFieldError('email')
                }}
                placeholder="you@yourfirm.co.uk"
                className={fieldClass('email')}
                autoComplete="email"
                inputMode="email"
                disabled={loading}
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && (
                <p className="text-xs text-casevo-danger mt-1">{fieldErrors.email}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full bg-casevo-primary text-white text-sm py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium flex items-center justify-center gap-2"
            >
              {loading && <Spinner />}
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
          </form>
          <p className="text-center text-sm text-casevo-mid mt-6">
            <button onClick={onShowLogin} className="text-casevo-brand underline">
              Back to login
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
export default RequestResetScreen
